import { useRef } from "react";
import Select, { components, GroupBase, MultiValue, OptionProps, StylesConfig, ValueContainerProps } from 'react-select';
import useInputValidation from "../../../hooks/input-validation-hook";
import { IValidationGroup } from "../../../hooks/validation-group/validation-group-hook";
import { DROPDOWN_MENU_WIDTH } from "../select-dropdown/select-dropdown";
import { rootContainer, TYPE } from "../../../services/container/root";
import IsMobileService from "../../../services/is-mobile-service/is-mobile-service";
import FontBody2 from "../../fonts/font-body2/font-body2";
import Icon from "../../icons/icon/icon";
import IconInfo from "../../icons/icon-info/icon-info";
import FontBody3 from "../../fonts/font-body3/font-body3";
import { FONT_COLOR } from "../../fonts/font-props";
import './select-dropdown-multi.scss';

/** TODO:: discuss if we can use this interface from regular dropdown component */
export interface IOption {
    value: string | number,
    label: any,
    description?: string
    disabled?: boolean
}

export interface ISelectDropdownMultiProps {
    options: IOption[],
    placeholder?: string,
    value?: MultiValue<IOption>,
    onChange?: (e: MultiValue<IOption>) => void;
    isLoading?: boolean;
    menuIsOpen?: boolean;
    menuWidth?: DROPDOWN_MENU_WIDTH;
    disabled?: boolean;
    required?: boolean;
    validationGroup?: IValidationGroup;
    styles?: StylesConfig<IOption, true>;
    isSearchable?: boolean;
    note?: string;
    target?: HTMLElement | null;
    closeMenuOnSelect?: boolean;
    hideSelectedOptions?: boolean;
    isClearable?: boolean;
}

const Option: React.FC<OptionProps<IOption, true, GroupBase<IOption>>> = (props) => {
    return (
        <>
            <components.Option {...props}>
                <span className={`option-checkmark checked`}></span>
                {props.data.label}
            {
                props.data.description &&
                <div>
                    <FontBody3 color={FONT_COLOR.PRIMARY3_50}>{ props.data.description}</FontBody3>
                </div>
            }
            </components.Option>
        </>
    );
};

const ValueContainer: React.FC<ValueContainerProps<IOption>> = ({ children, ...props }) => {
    let [values, input] = children as any;

    if (Array.isArray(values)) {
        values = `${values.map(v => v.props.children).join(', ')}`;
    }

    return (
        <components.ValueContainer {...props}>
            <div className="select-dropdown-multi-value-wrapper">
                {input}
                {!input.props.value && <div className="select-dropdown-multi-value">{values}</div>}
            </div>
        </components.ValueContainer>
    );
};

export const SelectDropdownMulti: React.FC<ISelectDropdownMultiProps> = ({
    options,
    placeholder,
    value,
    onChange,
    isLoading,
    menuIsOpen,
    menuWidth = DROPDOWN_MENU_WIDTH.FIT_CONTENT,
    disabled = false,
    required,
    validationGroup,
    styles,
    isSearchable = true,
    note,
    /** 
     * when passed target is explicitly NULL - the drop down will be rendered inside default container 
     * when target is not passed at all - document.body will be used as the container
     * when passed target is html element - this element will be used as container
    */
    target = document.body,
    closeMenuOnSelect = false,
    hideSelectedOptions = false,
    isClearable = false
}) => {
    const isValid = useInputValidation({ value : value?.length ? value.length : '', validationGroup, required });
    const ref = useRef<HTMLDivElement | null>(null);
    const isPad = rootContainer.get<IsMobileService>(TYPE.IsMobileService).isIPad();

    return (
        <div ref={ref} className={`select-dropdown-multi${!isValid ? ' invalid' : ''}`}>
            <Select
                components={{ Option, ValueContainer }}
                isDisabled={disabled}
                isOptionDisabled={(option) => !!option.disabled}
                /** Select doesn't process undefined correctly, when null is passed works well */
                value={value === undefined ? [] : value}
                placeholder={placeholder}
                isSearchable={isSearchable && !isPad}
                className="select-dropdown-container"
                classNamePrefix="select-dropdown"
                options={options}
                onChange={onChange}
                isLoading={isLoading}
                menuIsOpen={menuIsOpen}
                menuPortalTarget={target}
                menuPosition="absolute"
                menuPlacement="auto"
                isMulti
                closeMenuOnSelect={closeMenuOnSelect}
                hideSelectedOptions={hideSelectedOptions}
                isClearable={isClearable}
                styles={{
                    ...styles,
                    menuPortal: base => {
                        return { ...base, zIndex: 1 }
                    },
                    menu: (base, state) => ({
                        ...base,
                        ...styles?.menu?.(base, state),
                        minWidth: ref.current?.clientWidth + 'px',
                        width: menuWidth,
                        maxWidth: '500px'
                    }),                
                }}
            />
            {note &&
                <div className='note'>
                    <Icon Component={IconInfo} />
                    <FontBody2>{note}</FontBody2>
                </div>
            }
        </div>
    );
};

export default SelectDropdownMulti;